"use client";

import type { QuestCompletion } from "@/components/QuestsV2/types";
import { usePassportProvider } from "@/context";
import { useAccessToken } from "@/hooks";
import { authorizedGet, authorizedPost } from "@/utils/request";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";

const GAME_ID = "11111111-1111-1111-1111-111111111111";

interface UserProfile {
  kyc_status: "pending" | "approved" | "rejected" | "none";
}

interface OnboardingMilestoneResult {
  shouldShowLockedImx: boolean;
  hasOnboardingJourneyExpired: boolean;
  canClaimReward: boolean;
  isLoading: boolean;
  checkKycStatus: () => boolean;
  claimOnboardingReward: () => Promise<boolean>;
  markUserEligible: () => Promise<void>;
}

const EXPIRY_DAYS = 7;

const ONBOARDING_MILESTONE_MARK_EXTERNAL_ID = "onboarding-milestone-mark";
const ONBOARDING_MILESTONE_AWARDED_EXTERNAL_ID = "onboarding-milestone-awarded";
const ONBOARDING_MILESTONE_DONE_EXTERNAL_ID = `${GAME_ID}-onboarding`;

export function useOnboardingMilestone(): OnboardingMilestoneResult {
  const { isLoggedIn } = usePassportProvider();
  const getAccessToken = useAccessToken();

  const [shouldShowLockedImx, setShouldShowLockedImx] = useState(false);
  const [canClaimReward, setCanClaimReward] = useState(false);

  const {
    data: questCompletions,
    mutate: mutateQuestCompletions,
    isLoading: isLoadingQuests,
  } = useSWR<{
    questIDCompletionPair: Map<string, QuestCompletion>;
  }>(
    isLoggedIn ? `onboardingMilestone${GAME_ID}` : null,
    async () => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("No access token");
      }
      const response = await authorizedGet<{
        result: QuestCompletion[];
      }>(`/v1/quests/quest-completions?game_id=${GAME_ID}`, accessToken);

      const completionsMap = new Map<string, QuestCompletion>();

      // All of these quests have a max alloc of one. so the len doesn't matter
      for (const completion of response.result) {
        completionsMap.set(completion.external_id, completion);
      }

      return {
        questIDCompletionPair: completionsMap,
      };
    },
    {
      revalidateOnFocus: true,
    },
  );

  const { data: userProfile, isLoading: isLoadingProfile } =
    useSWRImmutable<UserProfile>(
      isLoggedIn ? "/v2/rewards/profile" : null,
      async (path: string) => {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error("No access token");
        return authorizedGet<UserProfile>(path, accessToken);
      },
    );

  const checkKycStatus = useCallback(() => {
    return userProfile?.kyc_status === "approved";
  }, [userProfile]);

  const milestoneMarkCompletion = questCompletions?.questIDCompletionPair.get(
    ONBOARDING_MILESTONE_MARK_EXTERNAL_ID,
  );

  const onboardedMoreThan7DaysAgo = useMemo(() => {
    const onboardingEventCompletion =
      questCompletions?.questIDCompletionPair.get(
        ONBOARDING_MILESTONE_DONE_EXTERNAL_ID,
      );

    if (onboardingEventCompletion) {
      return false;
    }

    return Boolean(
      milestoneMarkCompletion &&
        new Date(milestoneMarkCompletion.latest_completion_time).getTime() <
          new Date().getTime() - EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    );
  }, [questCompletions, milestoneMarkCompletion]);

  const claimOnboardingReward = useCallback(async () => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
      return false;
    }

    await authorizedPost(
      "/v2/rewards/onboarding-quest",
      { quest_external_id: "onboarding-milestone-awarded" },
      accessToken,
    );

    await mutateQuestCompletions();
    return true;
  }, [getAccessToken, mutateQuestCompletions]);

  const markUserEligible = useCallback(async () => {
    const accessToken = await getAccessToken();

    if (!accessToken || milestoneMarkCompletion) return;

    await authorizedPost(
      "/v2/rewards/onboarding-quest",
      { quest_external_id: "onboarding-milestone-mark" },
      accessToken,
    );

    await mutateQuestCompletions();
  }, [getAccessToken, mutateQuestCompletions, milestoneMarkCompletion]);

  useEffect(() => {
    const milestoneMarkCompletion = questCompletions?.questIDCompletionPair.get(
      ONBOARDING_MILESTONE_MARK_EXTERNAL_ID,
    );

    const milestoneAwardedCompletion =
      questCompletions?.questIDCompletionPair.get(
        ONBOARDING_MILESTONE_AWARDED_EXTERNAL_ID,
      );

    const milestoneDoneCompletion = questCompletions?.questIDCompletionPair.get(
      ONBOARDING_MILESTONE_DONE_EXTERNAL_ID,
    );

    const showLockedIMX = Boolean(
      milestoneMarkCompletion &&
        !milestoneAwardedCompletion &&
        !onboardedMoreThan7DaysAgo,
    );
    const canClaimReward = Boolean(
      milestoneMarkCompletion && milestoneDoneCompletion,
    );

    setShouldShowLockedImx(showLockedIMX);
    setCanClaimReward(canClaimReward);
  }, [questCompletions, onboardedMoreThan7DaysAgo]);

  return {
    shouldShowLockedImx,
    hasOnboardingJourneyExpired: onboardedMoreThan7DaysAgo,
    canClaimReward,
    isLoading: isLoadingQuests || isLoadingProfile,
    claimOnboardingReward,
    markUserEligible,
    checkKycStatus,
  };
}
